import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion /
// mood_tracker). askBrain sends the message + this app's declared action catalog to the generic
// fleet chat brain (POST /api/apps/oriora_curio/chat) — ONE billed call returns ONE registered
// action + a reply; the app executes it locally so the collection stays on-device. The regex
// intent-router below is the FREE FALLBACK (offline / signed-out / out of funds) — chat never
// goes dead.
//
// This app has no client-side pre-LLM refusal fence (the COLLECTION fence — never state
// market/appraisal value, no buy/sell/trade advice — is enforced server-side in the recap/
// reflect/ideas prompts and mirrored in the app's own UI copy); there is nothing to run here
// before the routing call.
import { store, type Item, type ItemPatch, type AttrValues, type Collection,
  type CollectionPatch, type CollectionSet, type NewSet, type SetPatch, type ProvenanceEvent, type NewProvenanceEvent } from "../store";
import { BrainError, brainCall } from "../brain";
import type { ChatAction, FilterRow, ItemFilter } from "./actions";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn { role: "user" | "assistant"; content: string; rows?: FilterRow[]; }
export interface BrainResponse { reply: string; action: ChatAction; }
// The brain can return MULTIPLE actions for one message (e.g. "add these 3 coins"), executed in order.
export interface BrainResult { reply: string; actions: ChatAction[]; }

const HELP = "I can add items, search or filter, mark have/wishlist, set a value, summarize, and delete — just tell me.";
const QUERY_RE = /\b(show|find|list|search|which|filter|how\s+many|all)\b/i;

const none = (message: string): BrainResponse => ({ reply: "", action: { kind: "none", message } });

/** Forgiving name lookup within the open collection — tolerant of misspellings,
 *  dropped middle words, and word order (FLEET STANDARD, see ./fuzzy). Used to
 *  disambiguate edit/delete/research so a paraphrased item name still resolves. */
export async function findByName(collectionId: string | null, term: string): Promise<Item[]> {
  if (!term.trim()) return [];
  // Home chat (collectionId === null) resolves across ALL collections; in-collection scopes to it.
  const all = collectionId ? await store.listItems(collectionId) : await store.listItems();
  return findNamedMatches(all, term);
}

/** "Couldn't find X" enriched with the closest names ("did you mean …?"). */
async function notFoundFor(collectionId: string | null, term: string): Promise<string> {
  const all = collectionId ? await store.listItems(collectionId) : await store.listItems();
  return notFoundMessage(all, term);
}

/** Forgiving COLLECTION lookup by name (same fleet fuzzy standard as items). */
export async function findCollectionByName(term: string): Promise<Collection[]> {
  if (!term.trim()) return [];
  const all = await store.listCollections();
  return findNamedMatches(all, term);
}

/** Forgiving SET lookup — scoped to the open collection, or across all collections from HOME. */
async function findSetByName(collectionId: string | null, term: string): Promise<CollectionSet[]> {
  if (!term.trim()) return [];
  let all: CollectionSet[] = [];
  if (collectionId) all = await store.listSets(collectionId);
  else {
    const cols = await store.listCollections();
    for (const c of cols) all = all.concat(await store.listSets(c.id));
  }
  return findNamedMatches(all, term); // CollectionSet has `name`
}

const asDate = (v: unknown): string | undefined => (typeof v === "string" ? v.match(/\d{4}-\d{2}-\d{2}/)?.[0] : undefined);
const asIntOpt = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseInt(String(v ?? "").replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};

/** Pull an optional user-entered value out of free text, returning the leftover as the name. */
function extractCost(text: string): { name: string; cost: number | null } {
  let cost: number | null = null;
  let name = text;
  const m =
    text.match(/\b(?:worth|valued\s+at|value|cost|for|@)\s*\$?\s*([0-9]+(?:\.[0-9]+)?)/i) ||
    text.match(/\$\s*([0-9]+(?:\.[0-9]+)?)/);
  if (m) {
    cost = parseFloat(m[1]);
    name = text.replace(m[0], "").trim();
  }
  name = name.replace(/[,;]+\s*$/, "").replace(/\s{2,}/g, " ").trim();
  return { name, cost };
}

export async function askBrain(history: ChatTurn[], collectionId: string | null, onChunk?: (t: string) => void): Promise<BrainResult> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text, collectionId); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, collectionId, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below still
    // answers (single action); chat never goes dead.
    const r = await routeMessage(text, collectionId);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (item name text → id) locally, because the data
// lives here. Each collection has its OWN custom attribute schema (see buildContext), so
// the context tells the model what attribute keys exist for the CURRENTLY OPEN collection.

export const CHAT_ACTIONS = [
  { name: "add_item", description: "Add a new item. In the HOME chat (no collection open) you MUST also give the destination collection; inside a collection it defaults to that one.", params: { name: "REQUIRED — the item's name", cost: "optional numeric value the user entered for this item — never a market appraisal", wishlist: "true if this is a wishlist/want item; false or omit if the user already owns it", collection: "the collection to add it to — REQUIRED from the home chat, omit inside a collection" } },
  { name: "edit_item", description: "Change an existing item's value, have/wishlist status, source, condition, or custom attributes. Give at least one field.", params: { target: "REQUIRED — which item, name as the user wrote it", cost: "new user-entered value, only if changing it", wishlist: "true to move to wishlist/want, false to mark as owned/have — only if changing status", source: "new source text (where it came from), only if changing it", condition: "new condition label, only if changing it", attrs: "optional object of this collection's custom attribute values to set, keyed by the attribute keys given in the context" } },
  { name: "delete_item", description: "Delete one item (the app asks the user to confirm first).", params: { target: "REQUIRED — which item, name as the user wrote it" } },
  { name: "filter", description: "Search / list items by a LITERAL term the user gave ('show my 1893', 'list my wishlist'). NOT for questions that weigh or compare items ('which is roughest / best / most complete', 'what should I log next') — those need the logged data, so use summarize.", params: { status: 'optional: "have" (owned), "want" (wishlist), or "all"', text: "optional free text to match against name/source/condition/attribute values" } },
  { name: "summarize", description: "Narrate the collection — counts, value, set completeness, recent adds — and ANSWER questions about what's in it or what to log next. Observational only, grounded only in the user's own logged data.", params: {} },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (item photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "research_item", description: "Research ONE item in the open collection — the AI looks it up and writes a brief (what it is + facts + a ROUGH, framed value orientation) onto the item's notes. Use for 'research my 1921 Morgan dollar', 'look this up', 'what is this / tell me about it', 'find its value'.", params: { target: "REQUIRED — which item, name as the user wrote it" } },
  { name: "create_collection", description: "Create a NEW collection/catalogue. Use for 'start a coins collection', 'make a new catalogue for my vinyl', 'add a collection'.", params: { name: "REQUIRED — the collection's name", kind: "optional short kind, e.g. coins / vinyl / cards / rocks / watches" } },
  { name: "edit_collection", description: "Change a collection's name, kind, or notes. Give at least one field.", params: { target: "REQUIRED — which collection, name as the user wrote it", name: "new name, only if renaming", kind: "new kind, only if changing (coins/vinyl/cards/…)", notes: "new notes, only if changing" } },
  { name: "delete_collection", description: "Delete an ENTIRE collection/catalogue and everything in it (the app asks the user to confirm first). Use for 'delete my coins collection', 'remove the vinyl catalogue'.", params: { target: "REQUIRED — which collection, name as the user wrote it" } },
  { name: "add_set", description: "Create a SET inside a collection — a completeness group (e.g. '1909-1958 Lincoln cents'). In a collection it defaults to that one; from HOME name the collection.", params: { name: "REQUIRED — the set's name", collection: "the collection it belongs to — REQUIRED from HOME, omit inside a collection", target_size: "optional total number of items the complete set holds", notes: "optional note" } },
  { name: "edit_set", description: "Change a set's name, target size, or notes. Identify it by name. Give at least one field.", params: { target: "REQUIRED — which set, name as the user wrote it", name: "new name, only if renaming", target_size: "new target size, only if changing", notes: "new notes, only if changing" } },
  { name: "delete_set", description: "Delete one set (the app asks the user to confirm first). Items in it are NOT deleted, just ungrouped.", params: { target: "REQUIRED — which set, name as the user wrote it" } },
  { name: "add_provenance", description: "Log a provenance/history event on ONE item — acquired, appraised, restored, exhibited, sold-on, a story. Use for 'note that I bought the 1921 Morgan at a fair in 2019', 'add history to …'.", params: { target: "REQUIRED — which item, name as the user wrote it", text: "REQUIRED — what happened, in the user's words", date: "optional YYYY-MM-DD it happened", kind: "optional short kind, e.g. acquired/appraised/restored/exhibited/sold" } },
  { name: "delete_provenance", description: "Delete one provenance event from an item (the app asks the user to confirm first).", params: { target: "REQUIRED — which item, name as the user wrote it", date: "optional YYYY-MM-DD to pick which event" } },
  { name: "move_item", description: "Move ONE item from the open collection into a DIFFERENT collection. Use for 'move the 1921 Morgan to my Silver collection', 'put this in Vinyl'.", params: { target: "REQUIRED — which item to move (name as the user wrote it)", collection: "REQUIRED — the destination collection's name" } },
  { name: "suggest_collections", description: "Recommend a few collection ideas to START, for a new user or when they ask what to collect. Use for 'what should I collect', 'give me ideas', 'help me start', 'recommend a collection'.", params: {} },
  { name: "save_to_cloud", description: "Save the user's data, a note, or THIS conversation to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note.", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// The app's fence, sent to the brain as `scope` — lets ASSIST mode help proactively (build a collection,
// research items) while keeping the compliance limits hard (framed value only, no buy/sell facilitation).
const CURIO_SCOPE =
  "Oriora Curio is a personal collection catalogue (coins, vinyl, cards, rocks, watches, anything). WITHIN that domain you help proactively: add items (even several at once), create collections, and RESEARCH an item — facts + a ROUGH value orientation. HARD limits — never do these: state a market/appraisal value as DEFINITIVE fact (value is ALWAYS framed as 'commonly lists around $X, I'm an AI, get a real appraisal'); advise, recommend, or facilitate buying / selling / trading / where-to-buy-or-sell; marketplace links; crypto/NSFW.";

interface ServerChatItem { action?: string; name?: string; params?: Record<string, unknown> }
interface ServerChat { action?: string; params?: Record<string, unknown>; reply?: string; actions?: ServerChatItem[] }

async function buildContext(collectionId: string | null): Promise<string> {
  const parts: string[] = [];
  try {
    if (collectionId) {
      const col = await store.getCollection(collectionId);
      if (col) {
        parts.push(`Collection: "${col.name}" (${col.kind || "general"}).`);
        if (col.attr_schema.length) {
          const fields = col.attr_schema
            .map((f) => `${f.key} (${f.label}, ${f.kind}${f.options?.length ? ": " + f.options.join("/") : ""})`)
            .join(", ");
          parts.push(`Custom attribute fields: ${fields}.`);
        }
      }
      const items = await store.listItems(collectionId);
      parts.push(`${items.length} item${items.length === 1 ? "" : "s"} in this collection.`);
    } else {
      // HOME chat — no single collection open; help manage collections themselves.
      const cols = await store.listCollections();
      if (!cols.length) {
        parts.push("HOME (no collection open). The user has NO collections yet — offer to create one or suggest ideas for what to collect.");
      } else {
        const lines = await Promise.all(cols.map(async (c) => {
          const n = (await store.listItems(c.id)).length;
          return `"${c.name}" (${c.kind || "general"}, ${n} item${n === 1 ? "" : "s"})`;
        }));
        parts.push(`HOME (no collection open). The user's collections: ${lines.join("; ")}.`);
        parts.push("To add/research/move a specific item, resolve which collection by name; if the target collection is unclear, ask. Prefer collection-level help here (create, delete, move between, suggest, overview).");
      }
    }
  } catch { /* context is optional */ }
  return parts.join(" ");
}

async function serverChat(history: ChatTurn[], text: string, collectionId: string | null, onChunk?: (t: string) => void): Promise<BrainResult> {
  const _notes = getInstructions();
  const context = (await buildContext(collectionId)) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: CURIO_SCOPE, assist: true }, onChunk);
  const say = asStr(r.reply) ?? "";
  // The brain returns an ARRAY of actions (multi-item in one message → one each). Map each to a
  // resolved on-device ChatAction. Back-compat: a single {action, params} still works.
  const list: ServerChatItem[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);
  const mapped: ChatAction[] = [];
  for (const it of list) {
    const single = await toAction({ action: it.action ?? it.name ?? "", params: it.params, reply: "" }, collectionId);
    mapped.push(single.action);
  }
  if (mapped.length === 0) return { reply: say || HELP, actions: [{ kind: "none" }] };
  return { reply: say, actions: mapped };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asNum = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : undefined;
};
const asBool = (v: unknown): boolean | undefined => {
  if (typeof v === "boolean") return v;
  const s = String(v ?? "").toLowerCase().trim();
  if (["true", "yes", "want", "wishlist", "1"].includes(s)) return true;
  if (["false", "no", "have", "own", "owned", "0"].includes(s)) return false;
  return undefined;
};
const asAttrs = (v: unknown): AttrValues | undefined => {
  if (v && typeof v === "object" && !Array.isArray(v)) {
    const out: AttrValues = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (val === null || typeof val === "string" || typeof val === "number" || typeof val === "boolean") out[k] = val;
    }
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
};

export async function toAction(r: ServerChat, collectionId: string | null): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_item": {
      const name = asStr(p.name);
      if (!name) return none(say || "What should I call the item?");
      const cost = asNum(p.cost);
      const wishlist = asBool(p.wishlist) ?? false;
      let targetCid = collectionId;
      if (!targetCid) {
        // HOME chat — no open collection, so the item must name a target collection.
        const colName = asStr(p.collection) ?? asStr(p.into) ?? asStr(p.to);
        if (!colName) return none(say || "Which collection should I add it to? Open one, or name the collection.");
        const cols = await findCollectionByName(colName);
        if (cols.length > 1) return none(`Several collections match "${colName}" — be more specific.`);
        if (cols.length === 1) targetCid = cols[0].id;
        // 0 matches: do NOT fail here — a sibling create_collection in this same turn may be
        // about to create it. Carry the NAME; executeAction resolves it after the create ran.
        else return { reply: say, action: { kind: "add_item", name, cost: cost ?? null, wishlist, collectionName: colName } };
      }
      return { reply: say, action: { kind: "add_item", name, cost: cost ?? null, wishlist, collectionId: targetCid } };
    }
    case "edit_item": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item should I update?");
      const rows = await findByName(collectionId, target);
      if (rows.length === 0) return none(await notFoundFor(collectionId, target));
      if (rows.length > 1) return none(`Several items match "${target}" — be more specific.`);
      const patch: ItemPatch = {};
      const bits: string[] = [];
      const cost = asNum(p.cost);
      if (cost !== undefined) { patch.cost = cost; bits.push(`value → ${cost}`); }
      const wishlist = asBool(p.wishlist);
      if (wishlist !== undefined) { patch.wishlist = wishlist; bits.push(wishlist ? "wishlist" : "have"); }
      const source = asStr(p.source);
      if (source) { patch.source = source; bits.push("source"); }
      const condition = asStr(p.condition);
      if (condition) { patch.condition = condition; bits.push("condition"); }
      const attrs = asAttrs(p.attrs);
      if (attrs) { patch.attrs = attrs; bits.push("attributes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change?");
      return { reply: say, action: { kind: "edit_item", id: rows[0].id, patch, label: `${rows[0].name}${bits.length ? " (" + bits.join(" · ") + ")" : ""}` } };
    }
    case "delete_item": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item do you want to delete?");
      const rows = await findByName(collectionId, target);
      if (rows.length === 0) return none(await notFoundFor(collectionId, target));
      if (rows.length > 1) return none(`Several items match "${target}" — be more specific.`);
      return { reply: say, action: { kind: "delete_item", id: rows[0].id, label: rows[0].name } };
    }
    case "filter": {
      const filter: ItemFilter = {};
      const status = asStr(p.status)?.toLowerCase();
      if (status === "have" || status === "want" || status === "all") filter.status = status;
      const text = asStr(p.text);
      if (text) filter.text = text;
      return { reply: "", action: { kind: "filter", filter, label: say || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize" } };
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "research_item": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item should I research? Give me its name.");
      const rows = await findByName(collectionId, target);
      if (rows.length === 0) return none(await notFoundFor(collectionId, target));
      if (rows.length > 1) return none(`Several items match "${target}" — be more specific.`);
      const row = rows[0];
      let collectionKind: string | undefined;
      try { const col = await store.getCollection(row.collection_id); collectionKind = col?.kind || undefined; } catch { /* kind is optional */ }
      return { reply: say, action: { kind: "research_item", id: row.id, itemName: row.name, collectionKind, attrs: row.attrs } };
    }
    case "create_collection": {
      const name = asStr(p.name);
      if (!name) return none(say || "What should I call the new collection?");
      return { reply: say, action: { kind: "create_collection", name, collectionKind: asStr(p.kind) } };
    }
    case "delete_collection": {
      const target = asStr(p.target) ?? asStr(p.name) ?? asStr(p.collection);
      if (!target) return none(say || "Which collection should I delete?");
      const rows = await findCollectionByName(target);
      if (rows.length === 0) return none(`Couldn't find a collection called "${target}".`);
      if (rows.length > 1) return none(`Several collections match "${target}" — be more specific.`);
      return { reply: say, action: { kind: "delete_collection", id: rows[0].id, label: rows[0].name } };
    }
    case "move_item": {
      const target = asStr(p.target);
      const dest = asStr(p.collection) ?? asStr(p.destination) ?? asStr(p.to);
      if (!target) return none(say || "Which item should I move?");
      if (!dest) return none(say || "Which collection should I move it to?");
      const items = await findByName(collectionId, target);
      if (items.length === 0) return none(await notFoundFor(collectionId, target));
      if (items.length > 1) return none(`Several items match "${target}" — be more specific.`);
      const cols = await findCollectionByName(dest);
      if (cols.length === 0) return none(`Couldn't find a collection called "${dest}".`);
      if (cols.length > 1) return none(`Several collections match "${dest}" — be more specific.`);
      if (cols[0].id === items[0].collection_id) return none(`"${items[0].name}" is already in ${cols[0].name}.`);
      return { reply: say, action: { kind: "move_item", id: items[0].id, itemLabel: items[0].name, toCollectionId: cols[0].id, toLabel: cols[0].name } };
    }
    case "edit_collection": {
      const target = asStr(p.target) ?? asStr(p.name) ?? asStr(p.collection);
      if (!target) return none(say || "Which collection should I update?");
      const rows = await findCollectionByName(target);
      if (rows.length === 0) return none(`Couldn't find a collection called "${target}".`);
      if (rows.length > 1) return none(`Several collections match "${target}" — be more specific.`);
      const patch: CollectionPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name && name.toLowerCase() !== rows[0].name.toLowerCase()) { patch.name = name; bits.push(`name → ${name}`); }
      const kind = asStr(p.kind); if (kind) { patch.kind = kind; bits.push(`kind → ${kind}`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, kind, or notes?");
      return { reply: say, action: { kind: "edit_collection", id: rows[0].id, patch, label: `${rows[0].name}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "add_set": {
      const name = asStr(p.name);
      if (!name) return none(say || "What should the set be called?");
      let cid = collectionId;
      if (!cid) {
        const colName = asStr(p.collection) ?? asStr(p.into) ?? asStr(p.to);
        if (!colName) return none(say || "Which collection is this set in? Open one, or name the collection.");
        const cols = await findCollectionByName(colName);
        if (cols.length === 0) return none(`Couldn't find a collection called "${colName}".`);
        if (cols.length > 1) return none(`Several collections match "${colName}" — be more specific.`);
        cid = cols[0].id;
      }
      const input: NewSet = { collection_id: cid, name };
      const target = asIntOpt(p.target_size); if (target !== undefined) input.target_size = target;
      const notes = asStr(p.notes); if (notes) input.notes = notes;
      let label: string | undefined;
      try { const col = await store.getCollection(cid); label = col?.name; } catch { /* label optional */ }
      return { reply: say, action: { kind: "add_set", input, label } };
    }
    case "edit_set": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which set should I update?");
      const rows = await findSetByName(collectionId, target);
      if (rows.length === 0) return none(`Couldn't find a set called "${target}".`);
      if (rows.length > 1) return none(`Several sets match "${target}" — be more specific:\n${rows.slice(0, 5).map((s) => `• ${s.name}`).join("\n")}`);
      const patch: SetPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push(`name → ${name}`); }
      const tgt = asIntOpt(p.target_size); if (tgt !== undefined) { patch.target_size = tgt; bits.push(`target → ${tgt}`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, target size, or notes?");
      return { reply: say, action: { kind: "edit_set", id: rows[0].id, patch, label: `${rows[0].name}${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_set": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which set should I delete?");
      const rows = await findSetByName(collectionId, target);
      if (rows.length === 0) return none(`Couldn't find a set called "${target}".`);
      if (rows.length > 1) return none(`Several sets match "${target}" — be more specific:\n${rows.slice(0, 5).map((s) => `• ${s.name}`).join("\n")}`);
      return { reply: say, action: { kind: "delete_set", id: rows[0].id, label: `the "${rows[0].name}" set` } };
    }
    case "add_provenance": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item is this history for?");
      const text = asStr(p.text) ?? asStr(p.note);
      if (!text) return none(say || "What happened? Tell me the event to log.");
      const rows = await findByName(collectionId, target);
      if (rows.length === 0) return none(await notFoundFor(collectionId, target));
      if (rows.length > 1) return none(`Several items match "${target}" — be more specific.`);
      const input: NewProvenanceEvent = { item_id: rows[0].id, text };
      const date = asDate(p.date); if (date) input.occurred_on = date;
      const kind = asStr(p.kind); if (kind) input.kind = kind;
      return { reply: say, action: { kind: "add_provenance", input, label: rows[0].name } };
    }
    case "delete_provenance": {
      const target = asStr(p.target);
      if (!target) return none(say || "Which item's history do you mean?");
      const rows = await findByName(collectionId, target);
      if (rows.length === 0) return none(await notFoundFor(collectionId, target));
      if (rows.length > 1) return none(`Several items match "${target}" — be more specific.`);
      const events = await store.listProvenanceEvents(rows[0].id);
      if (events.length === 0) return none(`No provenance events logged for "${rows[0].name}".`);
      const date = asDate(p.date);
      const cands = date ? events.filter((e) => e.occurred_on.startsWith(date)) : events;
      if (cands.length === 0) return none(`No provenance event on ${date} for "${rows[0].name}".`);
      if (cands.length > 1) {
        const list = cands.slice(0, 5).map((e: ProvenanceEvent) => `• ${e.occurred_on || "(no date)"} — ${e.text.slice(0, 40)}`).join("\n");
        return none(`Several events match — tell me which by date:\n${list}`);
      }
      return { reply: say, action: { kind: "delete_provenance", id: cands[0].id, label: `the ${cands[0].occurred_on || ""} event on "${rows[0].name}"`.replace(/\s+/g, " ").trim() } };
    }
    case "suggest_collections":
      return { reply: say, action: { kind: "suggest_collections" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string, collectionId: string | null): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // summarize / recap — the only path that touches the server brain
  if (/\b(summar(?:y|ise|ize)|overview|recap|reflect|insight)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // add / log / record a new item
  const addM = t.match(/^(?:add|log|record|new|create)\s+(?:an?\s+|the\s+)?(.+)$/i);
  if (addM) {
    let rest = addM[1].trim();
    const wishlist = /\b(want|wish|wishlist)\b/i.test(rest);
    rest = rest.replace(/\b(?:to\s+(?:my\s+)?wishlist|as\s+(?:a\s+)?want|want(?:ed)?|wishlist)\b/gi, "").trim();
    const { name, cost } = extractCost(rest);
    const finalName = (name || rest).replace(/[.,;]+$/, "").trim();
    if (!finalName) return { reply: "", action: { kind: "none", message: "What should I call the item?" } };
    return { reply: "", action: { kind: "add_item", name: finalName, cost, wishlist } };
  }

  // mark <item> as have / wishlist
  const markM = t.match(/^(?:mark|set|move)\s+(.+?)\s+(?:as\s+|to\s+)?(have|owned|own|wishlist|want|wanted)$/i);
  if (markM) {
    const rows = await findByName(collectionId, markM[1]);
    const wishlist = /want|wish/i.test(markM[2]);
    if (rows.length === 1) return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { wishlist }, label: rows[0].name } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await notFoundFor(collectionId, markM[1]) } };
    return { reply: "", action: { kind: "none", message: `Several items match "${markM[1].trim()}" — be more specific.` } };
  }

  // set value/worth of <item> to N
  const valM = t.match(/^(?:set\s+)?(?:the\s+)?(?:value|worth|cost)\s+(?:of\s+)?(.+?)\s+(?:to|=|at)\s+\$?\s*([0-9]+(?:\.[0-9]+)?)$/i);
  if (valM) {
    const rows = await findByName(collectionId, valM[1]);
    const cost = parseFloat(valM[2]);
    if (rows.length === 1) return { reply: "", action: { kind: "edit_item", id: rows[0].id, patch: { cost }, label: rows[0].name } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await notFoundFor(collectionId, valM[1]) } };
    return { reply: "", action: { kind: "none", message: `Several items match "${valM[1].trim()}" — be more specific.` } };
  }

  // delete / remove <item>  (executed only after HITL confirm in Chat.tsx)
  const delM = t.match(/^(?:delete|remove|drop)\s+(?:the\s+)?(.+)$/i);
  if (delM) {
    const rows = await findByName(collectionId, delM[1]);
    if (rows.length === 1) return { reply: "", action: { kind: "delete_item", id: rows[0].id, label: rows[0].name } };
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: await notFoundFor(collectionId, delM[1]) } };
    return { reply: "", action: { kind: "none", message: `Several items match "${delM[1].trim()}" — be more specific.` } };
  }

  // search / filter
  if (QUERY_RE.test(low)) {
    const filter: ItemFilter = {};
    if (/\b(wishlist|want|wanted)\b/i.test(low)) filter.status = "want";
    else if (/\b(have|owned|own)\b/i.test(low)) filter.status = "have";
    const term = t
      .replace(QUERY_RE, " ")
      .replace(/\b(items?|all|my|wishlist|want(?:ed)?|have|owned|own|in|the|do|i)\b/gi, " ")
      .replace(/[?.!]/g, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
    if (term) filter.text = term;
    return { reply: "", action: { kind: "filter", filter, label: t } };
  }

  return { reply: "", action: { kind: "none", message: HELP } };
}

// Standing instructions (fleet standard): the customer shapes the app through chat; stored
// on-device, injected into every brain call once this app runs the server-first chat.
const INSTRUCTIONS_KEY = "oriora.curio.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
